import { ChevronRight, MoreHoriz } from "@mui/icons-material";
import { IconButton, ListItemIcon, ListItemText, Menu, MenuItem } from "@mui/material";
import { useMemo, useRef, useState } from "react";
import { BaseModalComponent } from "../BaseModalComponent";
import { colors, alphaVar } from "../../ui/colors";
import { AccessGate } from "../../../permissions/access/AccessGate";
import type { AccessDecision, AccessBehaviorProps } from "../../../permissions/access/AccessGate.types";
import ReadMoreIcon from "@mui/icons-material/ReadMore";

export interface ActionItemAccessGateConfig {
  strategy: AccessDecision;
  behavior: React.ComponentType<AccessBehaviorProps>;
  behaviorConfig?: Record<string, unknown>;
}

interface ActionItem {
  label: string;
  icon: React.ReactNode;
  modalComponent?: React.ReactNode;
  modalKey?: string;
  subActions?: ActionItem[];
  onConfirmClose?: () => void;
  onClick?: () => void;
  accessGate?: ActionItemAccessGateConfig;
}

interface WidgetMenuActionsProps {
  actionList: ActionItem[];
  menuIcon?: React.ReactNode;
  sx?: object;
}

const flattenModalActions = (actions: ActionItem[]): ActionItem[] =>
  actions.flatMap((action) => [
    ...(action.modalComponent && action.modalKey ? [action] : []),
    ...(action.subActions ? flattenModalActions(action.subActions) : []),
  ]);

const flattenAccessGateModals = (actions: ActionItem[]): ActionItemAccessGateConfig["behaviorConfig"][] =>
  actions.flatMap((action) => {
    const configs: ActionItemAccessGateConfig["behaviorConfig"][] = [];
    const behaviorConfig = action.accessGate?.behaviorConfig as any;
    if (behaviorConfig?.modalKey && behaviorConfig?.component) {
      configs.push(behaviorConfig);
    }
    if (action.subActions) {
      configs.push(...flattenAccessGateModals(action.subActions));
    }
    return configs;
  });

export const WidgetMenuActions = ({ actionList, menuIcon, sx }: WidgetMenuActionsProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [subAnchorEl, setSubAnchorEl] = useState<HTMLElement | null>(null);
  const [activeSubIndex, setActiveSubIndex] = useState<number | null>(null);

  const triggerRefsMap = useRef<Record<string, React.RefObject<HTMLButtonElement>>>({});

  const modalActions = useMemo(() => flattenModalActions(actionList), [actionList]);
  const accessGateModalConfigs = useMemo(() => flattenAccessGateModals(actionList), [actionList]);

  modalActions.forEach(({ modalKey }) => {
    if (modalKey && !triggerRefsMap.current[modalKey]) {
      triggerRefsMap.current[modalKey] = { current: null };
    }
  });

  accessGateModalConfigs.forEach((config) => {
    const modalKey = (config as any).modalKey;
    if (modalKey && !triggerRefsMap.current[modalKey]) {
      triggerRefsMap.current[modalKey] = { current: null };
    }
  });

  const closeSubMenu = () => {
    setSubAnchorEl(null);
    setActiveSubIndex(null);
  };

  const closeMenu = () => {
    setAnchorEl(null);
    closeSubMenu();
  };

  const handleActionClick = (action: ActionItem) => {
    const triggerRef = action.modalKey ? triggerRefsMap.current[action.modalKey] : null;

    if (triggerRef?.current) {
      action.onClick?.();
      triggerRef.current.click();
      setTimeout(closeMenu, 0);
    } else if (action.onClick) {
      action.onClick();
      closeMenu();
    }
  };

  const renderMenuItem = (action: ActionItem, index: number) => {
    const hasSubActions = Boolean(action.subActions?.length);

    const menuItem = (
      <MenuItem
        key={action.modalKey ?? action.label}
        onClick={(e) => {
          if (hasSubActions) {
            setSubAnchorEl(e.currentTarget);
            setActiveSubIndex(index);
          } else {
            handleActionClick(action);
          }
        }}
      >
        <ListItemIcon>{action.icon}</ListItemIcon>
        <ListItemText primary={action.label} />
        {hasSubActions && <ChevronRight fontSize="small" />}
      </MenuItem>
    );

    if (action.accessGate) {
      return (
        <AccessGate
          key={action.modalKey ?? action.label}
          strategy={action.accessGate.strategy}
          behavior={action.accessGate.behavior}
          behaviorConfig={{
            ...action.accessGate.behaviorConfig,
            onIntercept: () => {
              const modalKey = (action.accessGate?.behaviorConfig as any)?.modalKey;
              const triggerRef = modalKey ? triggerRefsMap.current[modalKey] : null;

              if (triggerRef?.current) {
                triggerRef.current.click();
                setTimeout(closeMenu, 0);
              } else {
                closeMenu();
              }
            },
          }}
        >
          {menuItem}
        </AccessGate>
      );
    }

    return menuItem;
  };

  const activeSubActions = activeSubIndex !== null ? (actionList[activeSubIndex]?.subActions ?? []) : [];

  return (
    <>
      <IconButton
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{
          color: colors.gray[300],
          transition: "all 0.2s ease-in-out",
          "&:hover": { backgroundColor: alphaVar(colors.orange.base, 0.08), color: "var(--fx-surface-text, #0F172A)" },
        }}
      >
        {menuIcon ?? <ReadMoreIcon />}
      </IconButton>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={closeMenu} sx={sx}>
        {actionList.map((action, index) => renderMenuItem(action, index))}
      </Menu>

      <Menu
        anchorEl={subAnchorEl}
        open={Boolean(subAnchorEl)}
        onClose={closeSubMenu}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        sx={sx}
      >
        {activeSubActions.map((action, index) => renderMenuItem(action, index))}
      </Menu>

      {modalActions.map((action) => {
        return (
          <BaseModalComponent
            key={action.modalKey}
            modalLabel={action.label}
            defaultSizes={{
              width: "75%",
              height: "80%",
            }}
            modalKey={action.modalKey!}
            onConfirmClose={action.onConfirmClose}
            preferModalContent={action.modalKey === "Dashboard-Jobs-Calendar"}
            elementTrigger={
              <button ref={triggerRefsMap.current[action.modalKey!]} type="button" style={{ position: "absolute", left: -99999, top: -99999 }} />
            }
          >
            {action.modalComponent}
          </BaseModalComponent>
        );
      })}

      {accessGateModalConfigs.map((config: any) => (
        <BaseModalComponent
          key={config.modalKey}
          modalLabel={config.modalLabel}
          modalKey={config.modalKey}
          defaultSizes={config.defaultSizes}
          defaultFullscreen={config.defaultFullscreen}
          elementTrigger={
            <button ref={triggerRefsMap.current[config.modalKey]} type="button" style={{ position: "absolute", left: -99999, top: -99999 }} />
          }
        >
          <config.component {...(config.componentProps || {})} />
        </BaseModalComponent>
      ))}
    </>
  );
};
